import axios from "axios";
import { store } from "@services/redux/store";
import { apiSend, getRoute } from "@services/axios";
import {
  updateFlightStepBulk,
  updateFlightStepField,
} from "@services/redux/slices/flightSlice";

/**
 * Main function to handle flight product submissions by step level
 * @param {Object} flightData - Product data for the current step
 * @param {number} level - Step level (1, 2, or 3)
 * @returns {Promise} API response
 */
export const postFlight = async (flightId = null, flightData, level) => {
  switch (level) {
    case 1:
      return await postFlightStep1(flightData);
    case 2:
      return await postFlightStep2(flightId, flightData);
    case 3:
      return await postFlightStep3(flightId, flightData);
    case 4:
      return await postFlightStep4(flightId, flightData);
    default:
      throw new Error(`Unsupported level: ${level} for flight product`);
  }
};

export default postFlight;

export const postFlightStep1 = async (flightData) => {
  try {
    const response = await apiSend(
      `${getRoute("CREATE_FLIGHT_STEP_1")}`,
      "post",
      flightData
    );
    console.log("Flight Step 1 Response:", response);
    handleBackendErrors(response.data);

    const backendFlights = response.data?.result?.flight || [];
    const currentState = store.getState().flight;

    // Sync product_flight_id across steps 2, 3, and 4
    const syncedFlights = syncFlightsAfterStep1(backendFlights, currentState);
    store.dispatch(
      updateFlightStepBulk({ step: "step2", data: syncedFlights.step2 })
    );
    store.dispatch(
      updateFlightStepBulk({ step: "step3", data: syncedFlights.step3 })
    );
    store.dispatch(
      updateFlightStepBulk({ step: "step4", data: syncedFlights.step4 })
    );
    localStorage.setItem("product_id_save", response?.data?.result?.product_id);

    return response?.data?.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 2 - UPDATE FLIGHT DETAILS
// ====================================
export const postFlightStep2 = async (flightId, flightData) => {
  flightData = store.getState().flight?.step2;
  console.log("Submitting Flight Step 2 Data:", flightData);
  try {
    const response = await apiSend(
      `${getRoute("CREATE_FLIGHT_STEP_2")}/${flightId}`,
      "post",
      flightData
    );
    console.log("Flight Step 2 Response:", response);
    handleBackendErrors(response.data);

    const backendFlights = response.data?.result?.flight || [];
    const localStep3Flights = store.getState().flight?.step3?.flight || [];
    const localStep4Flights = store.getState().flight?.step4?.flight || [];
    const synced = syncStep3AndStep4WithStep2(
      backendFlights,
      localStep3Flights,
      localStep4Flights
    );

    store.dispatch(updateFlightStepBulk({ step: "step3", data: synced.step3 }));
    store.dispatch(updateFlightStepBulk({ step: "step4", data: synced.step4 }));
    return response?.data?.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 3 - FINAL PRICING + POLICIES
// ====================================
export const postFlightStep3 = async (flightId, flightData) => {
  const productType = "Flight";
  flightData = store.getState()[productType.toLowerCase()]?.step3;
  console.log("Submitting Flight Step 3 Data:", flightData);
  try {
    const response = await apiSend(
      `${getRoute("CREATE_FLIGHT_STEP_3")}/${flightId}`,
      "post",
      flightData
    );
    console.log("Flight Step 3 Response:", response);
    handleBackendErrors(response.data);

    const backendFlights = response.data?.result?.flight || [];
    const localStep4Flights = store.getState().flight?.step4?.flight || [];

    // Sync step4 after step3
    const syncedStep4Flights = syncStep4WithStep3Response(
      localStep4Flights,
      backendFlights
    );
    store.dispatch(
      updateFlightStepBulk({
        step: "step4",
        data: { flight: syncedStep4Flights },
      })
    );

    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

export const postFlightStep4 = async (flightId, flightData) => {
  const productType = "Flight";
  flightData = store.getState()[productType.toLowerCase()]?.step4;
  console.log("Submitting Flight Step 4 Data:", flightData);
  try {
    const response = await apiSend(
      `${getRoute("CREATE_FLIGHT_STEP_4")}/${flightId}`,
      "post",
      flightData
    );
    console.log("Flight Step 4 Response:", response);
    handleBackendErrors(response.data);
    return response?.data?.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// HELPER FUNCTIONS
// ====================================
function handleBackendErrors(data) {
  if (!data) return;

  // Case 0: Check if request status is false
  if (data?.request?.status === false) {
    const backendMsg = data?.request?.message || "Request failed";
    const backendError = data?.result?.Error;

    // If there's a detailed error object, print it
    if (backendError && typeof backendError === "object") {
      console.table(flattenBackendErrors(backendError));
    }

    throw new Error(backendMsg);
  }

  const backendMsg = data?.request?.message;
  const backendError = data?.result?.Error;

  // Case 1: Invalid request data → print the backend error table
  if (backendMsg && backendMsg.toLowerCase().includes("invalid request data")) {
    if (backendError && typeof backendError === "object") {
      console.table(flattenBackendErrors(backendError));
    }
    throw new Error(backendMsg);
  }

  // Case 2: Generic backend error object
  if (backendError && typeof backendError === "object") {
    const firstKey = Object.keys(backendError)[0];
    const firstMsg = backendError[firstKey]?.[0] || "Unknown backend error";
    throw new Error(firstMsg);
  }
}

function formatBackendError(error) {
  if (error?.response?.data) {
    handleBackendErrors(error.response.data);
  }
  return new Error(
    error.message || "An unknown error occurred during flight submission."
  );
}

function flattenBackendErrors(errorObj) {
  const rows = [];
  for (const key in errorObj) {
    const value = errorObj[key];
    if (Array.isArray(value)) {
      value.forEach((msg) => rows.push({ field: key, message: msg }));
    } else if (typeof value === "object") {
      for (const subKey in value) {
        rows.push({ field: `${key}.${subKey}`, message: value[subKey] });
      }
    } else {
      rows.push({ field: key, message: value });
    }
  }
  return rows;
}

/**
 * Sync step3 and step4 flights with backend step2 response
 * Updates product_flight_id and product_flight_itinerary_id
 * Leaves segments and pricing untouched
 */
export const syncStep3AndStep4WithStep2 = (
  step2Flights,
  step3Flights,
  step4Flights
) => {
  // Sync step3 flights (has nested itineraries structure)
  const syncedStep3 = step3Flights.map((localFlight) => {
    const backendFlight = step2Flights.find(
      (f) => f.product_flight_id === localFlight.product_flight_id
    );
    if (!backendFlight) return localFlight;

    const localItineraries = Array.isArray(localFlight.itineraries)
      ? localFlight.itineraries
      : [localFlight.itineraries];
    const backendItineraries = Array.isArray(backendFlight.itineraries)
      ? backendFlight.itineraries
      : [backendFlight.itineraries];

    const syncedItineraries = localItineraries.map((localItin, idx) => {
      const backendItin = backendItineraries[idx];
      if (!backendItin) return localItin;
      return {
        ...localItin,
        product_flight_itinerary_id: backendItin.product_flight_itinerary_id,
      };
    });

    return {
      ...localFlight,
      product_flight_id: backendFlight.product_flight_id,
      itineraries:
        syncedItineraries.length > 1 ? syncedItineraries : syncedItineraries[0],
    };
  });

  // Sync step4 flights (has product_flight_itinerary_id directly on flight object)
  const syncedStep4 = step4Flights.map((localFlight) => {
    const backendFlight = step2Flights.find(
      (f) => f.product_flight_id === localFlight.product_flight_id
    );
    if (!backendFlight) return localFlight;

    // Get the first itinerary ID from backend (step4 uses single itinerary ID per flight)
    const backendItineraries = Array.isArray(backendFlight.itineraries)
      ? backendFlight.itineraries
      : [backendFlight.itineraries];
    const firstItineraryId = backendItineraries[0]?.product_flight_itinerary_id;

    return {
      ...localFlight,
      product_flight_id: backendFlight.product_flight_id,
      product_flight_itinerary_id:
        firstItineraryId || localFlight.product_flight_itinerary_id,
    };
  });

  return {
    step3: { flight: syncedStep3 },
    step4: { flight: syncedStep4 },
  };
};

export const syncFlightsAfterStep1 = (backendFlights, currentState) => {
  const syncIds = (localFlights) =>
    localFlights.map((localFlight, idx) => {
      const backendFlight = backendFlights[idx];
      if (!backendFlight) return localFlight;
      return {
        ...localFlight,
        product_flight_id: backendFlight.product_flight_id,
        itineraries: localFlight.itineraries
          ? Array.isArray(localFlight.itineraries)
            ? localFlight.itineraries.map((itin) => ({
                ...itin,
                product_flight_itinerary_id: backendFlight.product_flight_id,
              }))
            : {
                ...localFlight.itineraries,
                product_flight_itinerary_id: backendFlight.product_flight_id,
              }
          : undefined,
      };
    });

  return {
    step2: { flight: syncIds(currentState.step2.flight) },
    step3: { flight: syncIds(currentState.step3.flight) },
    step4: { flight: syncIds(currentState.step4.flight) }, // ✅ added step4
  };
};

/**
 * Sync step4 flights with backend step3 response
 * Updates product_flight_id, product_flight_itinerary_id, and pricing
 */
export const syncStep4WithStep3Response = (
  step4Flights,
  backendStep3Flights
) => {
  return step4Flights.map((localFlight) => {
    const backendFlight = backendStep3Flights.find(
      (f) => f.product_flight_id === localFlight.product_flight_id
    );
    if (!backendFlight) return localFlight;

    return {
      ...localFlight,
      product_flight_id: backendFlight.product_flight_id,
      product_flight_itinerary_id: backendFlight.product_flight_itinerary_id,
    };
  });
};
